import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fetchBySlug, fetchCollection } from '../lib/cms';
import { ArrowLeft, Check, Zap, Cable, Wrench, Building2, Lightbulb } from 'lucide-react';

const iconMap = {
  Zap: Zap,
  Cable: Cable,
  Wrench: Wrench,
  Building2: Building2,
  Lightbulb: Lightbulb,
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [otherServices, setOtherServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBySlug('da', slug).then((data) => {
      if (!data) {
        navigate('/');
        return;
      }
      setService(data);
      setLoading(false);
    });

    fetchCollection('services_v2').then((data) => {
      setOtherServices(data.filter(s => s.slug_text.da !== slug).slice(0, 3));
    });
  }, [slug, navigate]);

  if (loading || !service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F0F0F]">
        <div className="w-12 h-12 border-4 border-[#C1121F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const Icon = iconMap[service.icon_name_text] || Zap;

  return (
    <div className="pt-28">
      {/* Content Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20">
            <div className="space-y-12">
              {/* Page Title & Intro */}
              <div>
                <div className="flex items-center space-x-4 mb-6">
                  <Icon className="w-8 h-8 text-black" />
                  <h1 className="text-3xl md:text-5xl font-bold text-black tracking-tight uppercase">
                    {service.title.da}
                  </h1>
                </div>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Professionelle el-løsninger med fokus på kvalitet og sikkerhed. Vi sikrer optimal drift i din hverdag.
                </p>
              </div>

              <div className="text-black">
                <h2 className="text-xl font-bold text-black mb-6 uppercase tracking-wider">Om ydelsen</h2>
                <div 
                  className="mt-12 mb-16 prose prose-p:text-black prose-p:text-xl prose-p:leading-relaxed prose-p:mb-6 max-w-none"
                  dangerouslySetInnerHTML={{ __html: service.description.da }}
                />
              </div>

              {service.features && (
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-8 uppercase tracking-wider">Vi hjælper blandt andet med:</h2>
                  <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8">
                    {/* Parsing the HTML list from CMS */}
                    {service.features.da.match(/<li>(.*?)<\/li>/g)?.map((item, i) => (
                      <div key={i} className="flex items-start space-x-3">
                        <Check className="w-5 h-5 text-gray-900 shrink-0 mt-0.5" />
                        <span className="text-gray-700 font-medium">{item.replace(/<\/?li>/g, '')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-8">
              <div className="sticky top-40">
                <div className="relative aspect-square md:aspect-video lg:aspect-square rounded-none overflow-hidden border border-gray-200 group">
                  <img
                    src="https://assets.auxodev.dk/assets/c790a797-f8bb-43f8-b6f5-1dd422b46a5f/5a5ca137-971c-44c0-9f87-30bb1617ab20.jpg"
                    alt={service.title.da}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                
                <div className="mt-12 p-8 bg-[#C1121F] rounded-none">
                  <h3 className="text-xl font-bold text-white mb-4 uppercase tracking-wider">Har du brug for hjælp?</h3>
                  <p className="text-white/80 mb-8">
                    Kontakt os i dag for en uforpligtende snak om dine behov. Vi svarer hurtigt på alle henvendelser.
                  </p>
                  <Link
                    to="/kontakt"
                    className="block w-full py-4 bg-white text-[#C1121F] text-center font-bold uppercase tracking-wider rounded-none hover:bg-gray-100 transition-colors"
                  >
                    Kontakt os nu
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-black mb-12 uppercase tracking-wider">Andre ydelser</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {otherServices.map((s) => {
              const OtherIcon = iconMap[s.icon_name_text] || Zap;
              return (
                <Link
                  key={s.id}
                  to={`/vi-tilbyder/${s.slug_text.da}`}
                  className="group block transition-all"
                >
                  <OtherIcon className="w-8 h-8 text-black mb-6" />
                  <h4 className="text-xl font-bold text-black mb-2 uppercase tracking-tight">{s.title.da}</h4>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                    {s.description.da.replace(/<[^>]*>/g, '')}
                  </p>
                  <div className="text-black text-xs font-bold uppercase tracking-widest flex items-center">
                    Læs mere <ArrowLeft className="ml-2 w-4 h-4 rotate-180 text-black" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
